// frontend/src/components/Analysis/StakeholdersSystemsTab.tsx
import React, { useState, useEffect } from 'react';
import { Users, Settings, Plus, Edit, Trash2, Loader } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../api';
import toast from 'react-hot-toast';

interface StakeholdersSystemsTabProps {
  synthesis: any;
  isAnalyzing?: boolean;
  analysisProgress?: any;
}

const StakeholdersSystemsTab: React.FC<StakeholdersSystemsTabProps> = ({
  synthesis,
  isAnalyzing,
  analysisProgress
}) => {
  const { currentProject } = useProject();

  // State for stakeholders
  const [stakeholders, setStakeholders] = useState<{ [key: string]: any }>({});
  const [editingStakeholder, setEditingStakeholder] = useState<string | null>(null);
  const [editStakeholderValue, setEditStakeholderValue] = useState({ role: '', responsibility: '' });
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const [newStakeholder, setNewStakeholder] = useState({ role: '', responsibility: '' });
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([]);

  // State for systems
  const [systems, setSystems] = useState<{ [key: string]: any }>({});
  const [editingSystem, setEditingSystem] = useState<string | null>(null);
  const [editSystemValue, setEditSystemValue] = useState({ system: '', purpose: '' });
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [newSystem, setNewSystem] = useState({ system: '', purpose: '' });
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track first load

  // Load data from synthesis or backend
  useEffect(() => {
    const loadData = async () => {
      if (!currentProject?.id) {
        setIsLoading(false);
        return;
      }

      // Show loader if no synthesis data yet (SOP not generated)
      if (!synthesis) {
        setIsLoading(true);
        return;
      }

      // Only show loader on first load, silent refresh afterwards
      if (!hasLoadedOnce) {
        setIsLoading(true);
      }

      try {
        let loadedStakeholders: { [key: string]: any } = {};
        let loadedSystems: { [key: string]: any } = {};

        // 🎯 PRIORITY 1: Load from latest process map (AI-generated fresh data)
        try {
          const processMapsRes = await api.get(`/api/process-maps/project/${currentProject.id}`);
          const processMaps = processMapsRes.data?.process_maps || [];

          // Get latest Level 3 process map (has most detailed stakeholder info)
          const latestL3Map = processMaps
            .filter((m: any) => m.level === 3)
            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

          if (latestL3Map?.map_data?.elements?.stakeholders) {
            const mapStakeholders = latestL3Map.map_data.elements.stakeholders;
            loadedStakeholders = mapStakeholders;
            console.log('🎯 PRIORITY 1: Loaded stakeholders from latest L3 process map:', Object.keys(mapStakeholders).length);
            console.log('📊 Process Map Stakeholders:', mapStakeholders);
          }
        } catch (error) {
          console.log('⚠️ Could not load from process maps, will try synthesis');
        }

        // 📋 PRIORITY 2: Load from synthesis (backup if process map not available)
        if (Object.keys(loadedStakeholders).length === 0 && synthesis) {
          const synthesisData = typeof synthesis.synthesis_text === 'string'
            ? JSON.parse(synthesis.synthesis_text)
            : synthesis.synthesis_text || synthesis;

          // Load stakeholders
          if (synthesisData?.stakeholders && Object.keys(synthesisData.stakeholders).length > 0) {
            loadedStakeholders = synthesisData.stakeholders;
            console.log('📋 PRIORITY 2: Stakeholders loaded from synthesis:', Object.keys(loadedStakeholders).length);
            console.log('📊 Synthesis Stakeholder data:', loadedStakeholders);
          }
        }

        // Load systems from synthesis (unchanged priority)
        if (synthesis) {
          const synthesisData = typeof synthesis.synthesis_text === 'string'
            ? JSON.parse(synthesis.synthesis_text)
            : synthesis.synthesis_text || synthesis;

          if (synthesisData?.systems && Object.keys(synthesisData.systems).length > 0) {
            loadedSystems = synthesisData.systems;
            console.log('✅ Systems loaded from synthesis:', Object.keys(loadedSystems).length);
          } else if (synthesisData?.systems_platforms) {
            loadedSystems = synthesisData.systems_platforms;
            console.log('✅ Systems loaded from synthesis.systems_platforms');
          } else if (synthesisData?.platforms) {
            loadedSystems = synthesisData.platforms;
            console.log('✅ Systems loaded from synthesis.platforms');
          }
        }

        // Fallback: Try backend API if synthesis data is empty
        if (Object.keys(loadedStakeholders).length === 0 || Object.keys(loadedSystems).length === 0) {
          const [stakeholdersRes, systemsRes] = await Promise.all([
            api.get(`/api/analysis/stakeholders/${currentProject.id}`).catch(() => ({ data: {} })),
            api.get(`/api/analysis/systems/${currentProject.id}`).catch(() => ({ data: {} }))
          ]);

          if (Object.keys(loadedStakeholders).length === 0) {
            loadedStakeholders = stakeholdersRes.data?.stakeholders || {};
          }
          if (Object.keys(loadedSystems).length === 0) {
            loadedSystems = systemsRes.data?.systems || {};
          }
        }

        // ✅ FIX: Remove USER_ prefix from stakeholder names for display
        const cleanStakeholders: { [key: string]: any } = {};
        Object.keys(loadedStakeholders).forEach(stakeholderName => {
          const cleanName = stakeholderName.replace(/^USER_/, '');
          cleanStakeholders[cleanName] = loadedStakeholders[stakeholderName];
        });

        console.log('Final loaded data:', {
          stakeholders: Object.keys(cleanStakeholders).length,
          systems: Object.keys(loadedSystems).length,
          cleanedStakeholders: Object.keys(cleanStakeholders)
        });

        setStakeholders(cleanStakeholders);
        setSystems(loadedSystems);
      } catch (error) {
        console.error('Error loading stakeholders/systems:', error);
      } finally {
        setIsLoading(false);
        setHasLoadedOnce(true); // Mark first load complete
      }
    };

    loadData();
  }, [currentProject?.id, synthesis]);

  // Auto-sync helper function - triggers manual sync logic for ALL process maps
  const autoSyncProcessMaps = async () => {
    if (!currentProject?.id) return;

    // Show loading toast
    const toastId = toast.loading('🔄 Auto-sync in progress...', {
      duration: Infinity
    });

    try {
      // Get all process maps for this project
      const response = await api.get(`/api/process-maps/project/${currentProject.id}`);
      const processMaps = response.data.process_maps || [];

      if (processMaps.length === 0) {
        console.log('No process maps to sync');
        toast.dismiss(toastId);
        return;
      }

      console.log(`🔄 Auto-syncing ${processMaps.length} process map(s)...`);

      // Sync each process map using the SAME logic as manual sync button
      let syncedCount = 0;
      let skippedCount = 0;
      for (const map of processMaps) {
        try {
          toast.loading(`🔄 Syncing process map ${syncedCount + skippedCount + 1}/${processMaps.length}...`, {
            id: toastId
          });
          const response = await api.post(`/api/analysis/process-maps/${map.id}/sync-with-stakeholders`);

          // ✅ FIX: Check if sync was skipped due to recent edits
          if (response.data.skipped) {
            console.log(`Sync skipped for map ${map.id} - has recent edits`);
            skippedCount++;
          } else {
            syncedCount++;
          }
        } catch (error) {
          console.error(`Failed to sync map ${map.id}:`, error);
        }
      }

      // ✅ FIX: Show success toast with skipped count if applicable
      const message = skippedCount > 0
        ? `✅ Auto-sync complete! ${syncedCount} synced, ${skippedCount} skipped (recently edited)`
        : `✅ Auto-sync complete! ${syncedCount}/${processMaps.length} process map(s) synced`;

      toast.success(message, {
        id: toastId,
        duration: 5000
      });

      // Trigger a custom event to notify Process Maps screen to reload
      window.dispatchEvent(new CustomEvent('process-maps-updated', {
        detail: { projectId: currentProject.id }
      }));

    } catch (error) {
      console.error('Auto-sync failed:', error);
      toast.error('Auto-sync failed', { id: toastId });
    }
  };

  // Save functions
  const saveStakeholders = async (updatedStakeholders: { [key: string]: any }) => {
    if (!currentProject?.id) return;
    try {
      // 1. Save stakeholders to database
      await api.put(`/api/analysis/stakeholders/${currentProject.id}`, {
        stakeholders: updatedStakeholders
      });

      // 2. Wait 500ms for synthesis update to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // 3. Auto-sync process maps with updated stakeholders
      await autoSyncProcessMaps();
    } catch (error) {
      console.error('Error saving stakeholders:', error);
      toast.error('Failed to save stakeholders');
    }
  };

  const saveSystems = async (updatedSystems: { [key: string]: any }) => {
    if (!currentProject?.id) return;
    try {
      await api.put(`/api/analysis/systems/${currentProject.id}`, {
        systems: updatedSystems
      });
    } catch (error) {
      console.error('Error saving systems:', error);
    }
  };

  // Stakeholder functions
  const startEditStakeholder = (role: string) => {
    const responsibility = stakeholders[role];
    let responsibilityText: string;
    if (typeof responsibility === 'string') {
      responsibilityText = responsibility;
    } else if (responsibility && typeof responsibility === 'object') {
      const respObj = responsibility as any;
      responsibilityText = respObj.responsibility || respObj.description || respObj.details || JSON.stringify(responsibility);
    } else {
      responsibilityText = String(responsibility || '');
    }
    setEditingStakeholder(role);
    setEditStakeholderValue({ role, responsibility: responsibilityText });
  };

  const saveStakeholderEdit = () => {
    if (!editingStakeholder) return;
    const updated = { ...stakeholders };
    if (editStakeholderValue.role !== editingStakeholder) {
      delete updated[editingStakeholder];
    }
    updated[editStakeholderValue.role] = editStakeholderValue.responsibility;
    setStakeholders(updated);
    saveStakeholders(updated);
    setEditingStakeholder(null);
  };

  const deleteStakeholder = (role: string) => {
    const updated = { ...stakeholders };
    delete updated[role];
    setStakeholders(updated);
    saveStakeholders(updated);
  };

  const addStakeholder = () => {
    if (!newStakeholder.role.trim() || !newStakeholder.responsibility.trim()) return;
    const updated = { ...stakeholders, [newStakeholder.role]: newStakeholder.responsibility };
    setStakeholders(updated);
    saveStakeholders(updated);
    setNewStakeholder({ role: '', responsibility: '' });
    setShowAddStakeholder(false);
  };

  const mergeStakeholders = () => {
    if (selectedStakeholders.length < 2) return;
    const merged: string[] = [];
    const newRole = selectedStakeholders.join(' / ');
    selectedStakeholders.forEach(role => {
      const resp = stakeholders[role];
      const respText = typeof resp === 'string' ? resp : (resp?.responsibility || resp?.description || '');
      if (respText) merged.push(respText);
    });
    const updated = { ...stakeholders };
    selectedStakeholders.forEach(role => delete updated[role]);
    updated[newRole] = merged.join('; ');
    setStakeholders(updated);
    saveStakeholders(updated);
    setSelectedStakeholders([]);
  };

  // System functions
  const startEditSystem = (system: string) => {
    const details = systems[system];
    let purposeText: string;
    if (typeof details === 'string') {
      purposeText = details;
    } else if (details && typeof details === 'object') {
      const detailsObj = details as any;
      purposeText = detailsObj.purpose || detailsObj.description || detailsObj.details || JSON.stringify(details);
    } else {
      purposeText = String(details || '');
    }
    setEditingSystem(system);
    setEditSystemValue({ system, purpose: purposeText });
  };

  const saveSystemEdit = () => {
    if (!editingSystem) return;
    const updated = { ...systems };
    if (editSystemValue.system !== editingSystem) {
      delete updated[editingSystem];
    }
    updated[editSystemValue.system] = editSystemValue.purpose;
    setSystems(updated);
    saveSystems(updated);
    setEditingSystem(null);
  };

  const deleteSystem = (system: string) => {
    const updated = { ...systems };
    delete updated[system];
    setSystems(updated);
    saveSystems(updated);
  };

  const addSystem = () => {
    if (!newSystem.system.trim() || !newSystem.purpose.trim()) return;
    const updated = { ...systems, [newSystem.system]: newSystem.purpose };
    setSystems(updated);
    saveSystems(updated);
    setNewSystem({ system: '', purpose: '' });
    setShowAddSystem(false);
  };

  const mergeSystems = () => {
    if (selectedSystems.length < 2) return;
    const merged: string[] = [];
    const newSystem = selectedSystems.join(' / ');
    selectedSystems.forEach(sys => {
      const details = systems[sys];
      const detailsText = typeof details === 'string' ? details : (details?.purpose || details?.description || '');
      if (detailsText) merged.push(detailsText);
    });
    const updated = { ...systems };
    selectedSystems.forEach(sys => delete updated[sys]);
    updated[newSystem] = merged.join('; ');
    setSystems(updated);
    saveSystems(updated);
    setSelectedSystems([]);
  };

  // Check if we should show loading state
  const analysisHasRun = analysisProgress && (analysisProgress.completed > 0 || analysisProgress.failed > 0);
  const isWaitingForData = !isAnalyzing && analysisHasRun && (Object.keys(stakeholders).length === 0 && Object.keys(systems).length === 0);
  const shouldShowLoading = isLoading || isAnalyzing || isWaitingForData;

  // Show info card if no synthesis data available
  if (!synthesis && !isAnalyzing) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="max-w-2xl w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 p-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Stakeholders & Systems Analysis
              </h3>
              <p className="text-gray-600 mb-3">
                This section will display the stakeholders and systems identified during the SOP generation process.
              </p>
              <div className="bg-white rounded-md p-4 border border-blue-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>To view this analysis:</strong>
                </p>
                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                  <li>Upload your process documents in the Documents tab</li>
                  <li>Run the analysis in the Analysis section</li>
                  <li>Generate the SOP in the SOP Generation tab</li>
                  <li>Return here to view stakeholders and systems</li>
                </ol>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                💡 The analysis will automatically identify all stakeholders, their responsibilities, and the systems/platforms involved in your process.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loader while analyzing or loading
  if (shouldShowLoading) {
    let message = 'Loading stakeholders and systems...';
    if (isAnalyzing) {
      message = 'Analyzing documents...';
    }

    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <p className="text-gray-600">{message}</p>
      </div>
    );
  }

  const hasStakeholders = Object.keys(stakeholders).length > 0;
  const hasSystems = Object.keys(systems).length > 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stakeholders & Responsibilities */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Users className="w-5 h-5 text-blue-600 mr-2" />
                  Stakeholders & Responsibilities
                </h3>
                <div className="flex items-center space-x-2">
                  {selectedStakeholders.length > 1 && (
                    <button
                      onClick={mergeStakeholders}
                      className="text-purple-600 hover:text-purple-800 p-1"
                      title="Merge selected stakeholders"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 5a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1zM6 12a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1zM4 16a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddStakeholder(!showAddStakeholder)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Add stakeholder"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Add Stakeholder Form */}
              {showAddStakeholder && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">Add New Stakeholder</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Role/Position"
                      value={newStakeholder.role}
                      onChange={(e) => setNewStakeholder({ ...newStakeholder, role: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      placeholder="Responsibilities"
                      value={newStakeholder.responsibility}
                      onChange={(e) => setNewStakeholder({ ...newStakeholder, responsibility: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={addStakeholder}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddStakeholder(false);
                          setNewStakeholder({ role: '', responsibility: '' });
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Stakeholders List */}
              <div className="space-y-3">
                {Object.keys(stakeholders).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No stakeholders identified yet. Click + to add a stakeholder.</p>
                ) : (
                  Object.entries(stakeholders).map(([role, responsibility]) => {
                  let responsibilityText: string;
                  if (typeof responsibility === 'string') {
                    responsibilityText = responsibility;
                  } else if (responsibility && typeof responsibility === 'object') {
                    const respObj = responsibility as any;
                    // ✅ FIX: Handle responsibilities array from validation_edit
                    if (Array.isArray(respObj.responsibilities)) {
                      // Join array items with newlines, filter out warning emoji
                      responsibilityText = respObj.responsibilities
                        .map((r: string) => r.replace(/^⚠️\s*/, '')) // Remove warning emoji
                        .join('\n');
                    } else {
                      responsibilityText = respObj.responsibility || respObj.description || respObj.details || JSON.stringify(responsibility);
                    }
                  } else {
                    responsibilityText = String(responsibility || '');
                  }

                  return (
                    <div key={role} className="group p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                      {editingStakeholder === role ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editStakeholderValue.role}
                            onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, role: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <textarea
                            value={editStakeholderValue.responsibility}
                            onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, responsibility: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={saveStakeholderEdit}
                              className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingStakeholder(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedStakeholders.includes(role)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedStakeholders([...selectedStakeholders, role]);
                                  } else {
                                    setSelectedStakeholders(selectedStakeholders.filter(r => r !== role));
                                  }
                                }}
                                className="mr-2"
                              />
                              <div className="font-medium text-gray-800">{role}</div>
                            </div>
                            <div className="text-sm text-gray-600 mt-1 ml-6">{responsibilityText}</div>
                          </div>
                          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditStakeholder(role)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit stakeholder"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteStakeholder(role)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete stakeholder"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
                )}
              </div>
            </div>
          </div>

        {/* Systems & Platforms */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Settings className="w-5 h-5 text-orange-600 mr-2" />
                  Systems & Platforms
                </h3>
                <div className="flex items-center space-x-2">
                  {selectedSystems.length > 1 && (
                    <button
                      onClick={mergeSystems}
                      className="text-purple-600 hover:text-purple-800 p-1"
                      title="Merge selected systems"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 5a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1zM6 12a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H7a1 1 0 0 1-1-1zM4 16a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddSystem(!showAddSystem)}
                    className="text-orange-600 hover:text-orange-800 p-1"
                    title="Add system"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              {/* Add System Form */}
              {showAddSystem && (
                <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-orange-800 mb-2">Add New System</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="System/Platform Name"
                      value={newSystem.system}
                      onChange={(e) => setNewSystem({ ...newSystem, system: e.target.value })}
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <textarea
                      placeholder="Purpose/Description"
                      value={newSystem.purpose}
                      onChange={(e) => setNewSystem({ ...newSystem, purpose: e.target.value })}
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      rows={2}
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={addSystem}
                        className="px-3 py-1 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowAddSystem(false);
                          setNewSystem({ system: '', purpose: '' });
                        }}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Systems List */}
              <div className="space-y-3">
                {Object.keys(systems).length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No systems identified yet. Click + to add a system.</p>
                ) : (
                  Object.entries(systems).map(([system, details]) => {
                  let purposeText: string;
                  if (typeof details === 'string') {
                    purposeText = details;
                  } else if (details && typeof details === 'object') {
                    const detailsObj = details as any;
                    purposeText = detailsObj.purpose || detailsObj.description || detailsObj.details || JSON.stringify(details);
                  } else {
                    purposeText = String(details || '');
                  }

                  return (
                    <div key={system} className="group p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                      {editingSystem === system ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editSystemValue.system}
                            onChange={(e) => setEditSystemValue({ ...editSystemValue, system: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <textarea
                            value={editSystemValue.purpose}
                            onChange={(e) => setEditSystemValue({ ...editSystemValue, purpose: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            rows={2}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={saveSystemEdit}
                              className="px-3 py-1 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingSystem(null)}
                              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedSystems.includes(system)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSystems([...selectedSystems, system]);
                                  } else {
                                    setSelectedSystems(selectedSystems.filter(s => s !== system));
                                  }
                                }}
                                className="mr-2"
                              />
                              <div className="font-medium text-gray-800">{system}</div>
                            </div>
                            <div className="text-sm text-gray-600 mt-1 ml-6">{purposeText}</div>
                          </div>
                          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditSystem(system)}
                              className="text-orange-600 hover:text-orange-800 p-1"
                              title="Edit system"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteSystem(system)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete system"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
                )}
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default StakeholdersSystemsTab;
